import axios from "axios";

const apiUrl = process.env.REACT_APP_BACKEND_URL;

export type WhatsAppIntegration = "WHATSAPP-BAILEYS" | "WHATSAPP-BUSINESS";

export type WhatsAppInstancePurpose = "workflow" | "crm" | "chatbot";

export interface WhatsAppInstance {
  agent_id: string | null;
  instance_name: string;
  instance_id: string | null;
  client_id: string | null;
  integration: WhatsAppIntegration | string;
  status: string;
  phone_number: string | null;
  business_id: string | null;
  app_id: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  last_status_at: string | null;
  is_current_for_client: boolean;
  /** Features this instance sends from for its client. Empty means it falls
   * back to the legacy single "current" instance above. */
  purposes: WhatsAppInstancePurpose[];
  /** Inbound voice (WhatsApp Business Calling) state. References only. */
  inbound_agent?: InboundAgentState | null;
}

export type InboundAgentStatus = "agent_attached" | "sip_connected" | "sip_error";

/** Meta side of a number's calling. The LiveKit side is the number's inbound
 * route: `route_status` "live" means calls are answered. */
export interface InboundAgentState {
  status: InboundAgentStatus | string | null;
  error?: string | null;
  agent_id?: string | null;
  version_id?: string | null;
  voice?: string | null;
  /** E.164, known once connected. */
  number?: string | null;
  leg?: string | null;
  password_updated_at?: string | null;
  route_status?: "pending" | "live" | "conflict" | "error" | string | null;
  route_detail?: string | null;
}

export interface InboundAgentAttachInput {
  agent_id: string;
  version_id: string;
  voice: string;
}

export interface QrCodeResult {
  instance_name: string;
  status: string;
  /** Base64 image. Expires in well under a minute, so never cache it. */
  qr_code: string | null;
  state: string | null;
}

export interface ConnectionState {
  instance_name: string;
  status: string;
  state: string | null;
}

export interface BusinessInstanceInput {
  client_id: string;
  /** Meta's numeric phone number ID from WhatsApp Manager, not a phone number. */
  phone_number_id: string;
  business_id: string;
  /** Meta App ID that owns the WABA — Resumable Upload sessions start on it. */
  app_id: string;
  /** Forwarded to Evolution and never stored by core; it cannot be read back. */
  access_token: string;
}

export interface WebhookCredential {
  instance_name: string;
  client_id: string;
  business_id: string | null;
  phone_number_id: string | null;
  has_app_secret: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export interface WebhookCredentialCreated extends WebhookCredential {
  /** Returned once at creation/rotation. Never readable again. */
  verify_token: string;
}

const auth = (token: string) => ({ headers: { Authorization: `Bearer ${token}` } });

export const whatsappApi = {
  attachAgent: async (token: string, instanceName: string, agentId: string | null): Promise<WhatsAppInstance> =>
    (await axios.put(`${apiUrl}/api/v2/settings/whatsapp-instances/${encodeURIComponent(instanceName)}/agent`, { agent_id: agentId }, auth(token))).data,

  attachInboundAgent: async (
    token: string,
    instanceName: string,
    payload: InboundAgentAttachInput
  ): Promise<InboundAgentState> =>
    (
      await axios.put(
        `${apiUrl}/api/v2/settings/whatsapp-instances/${encodeURIComponent(instanceName)}/inbound-voice`,
        payload,
        auth(token)
      )
    ).data,

  detachInboundAgent: async (token: string, instanceName: string): Promise<{ detached: boolean }> =>
    (
      await axios.post(
        `${apiUrl}/api/v2/settings/whatsapp-instances/${encodeURIComponent(instanceName)}/inbound-voice/detach-agent`,
        {},
        auth(token)
      )
    ).data,

  connectInboundVoice: async (
    token: string,
    instanceName: string,
    metaToken: string
  ): Promise<InboundAgentState> =>
    (
      await axios.post(
        `${apiUrl}/api/v2/settings/whatsapp-instances/${encodeURIComponent(instanceName)}/inbound-voice/connect`,
        {},
        { headers: { Authorization: `Bearer ${token}`, "X-Meta-Token": metaToken } }
      )
    ).data,

  rotateInboundSecret: async (
    token: string,
    instanceName: string,
    metaToken: string
  ): Promise<InboundAgentState> =>
    (
      await axios.post(
        `${apiUrl}/api/v2/settings/whatsapp-instances/${encodeURIComponent(instanceName)}/inbound-voice/rotate`,
        {},
        { headers: { Authorization: `Bearer ${token}`, "X-Meta-Token": metaToken } }
      )
    ).data,

  disconnectInboundVoice: async (
    token: string,
    instanceName: string,
    metaToken: string
  ): Promise<InboundAgentState> =>
    (
      await axios.post(
        `${apiUrl}/api/v2/settings/whatsapp-instances/${encodeURIComponent(instanceName)}/inbound-voice/disconnect`,
        {},
        { headers: { Authorization: `Bearer ${token}`, "X-Meta-Token": metaToken } }
      )
    ).data,

  readInboundVoice: async (token: string, instanceName: string): Promise<InboundAgentState> =>
    (
      await axios.get(
        `${apiUrl}/api/v2/settings/whatsapp-instances/${encodeURIComponent(instanceName)}/inbound-voice`,
        auth(token)
      )
    ).data,

  listForClient: async (token: string, clientId: string): Promise<WhatsAppInstance[]> => {
    const res = await axios.get(
      `${apiUrl}/api/v2/settings/whatsapp-instances`,
      auth(token)
    );
    return res.data?.instances || [];
  },

  createQrInstance: async (token: string, clientId: string): Promise<QrCodeResult> => {
    const res = await axios.post(
      `${apiUrl}/api/v2/settings/whatsapp-instances/qr`,
      { client_id: clientId },
      auth(token)
    );
    return res.data;
  },

  createBusinessInstance: async (
    token: string,
    payload: BusinessInstanceInput
  ): Promise<WhatsAppInstance> => {
    const res = await axios.post(
      `${apiUrl}/api/v2/settings/whatsapp-instances/business`,
      payload,
      auth(token)
    );
    return res.data;
  },

  refreshQr: async (token: string, instanceName: string): Promise<QrCodeResult> => {
    const res = await axios.get(
      `${apiUrl}/api/v2/settings/whatsapp-instances/${encodeURIComponent(instanceName)}/qr`,
      auth(token)
    );
    return res.data;
  },

  readState: async (token: string, instanceName: string): Promise<ConnectionState> => {
    const res = await axios.get(
      `${apiUrl}/api/v2/settings/whatsapp-instances/${encodeURIComponent(instanceName)}/state`,
      auth(token)
    );
    return res.data;
  },

  assignToClient: async (
    token: string,
    instanceName: string,
    clientId: string
  ): Promise<WhatsAppInstance> => {
    const res = await axios.post(
      `${apiUrl}/api/v2/settings/whatsapp-instances/${encodeURIComponent(instanceName)}/assign`,
      { client_id: clientId },
      auth(token)
    );
    return res.data;
  },

  deleteInstance: async (token: string, instanceName: string): Promise<void> => {
    await axios.delete(
      `${apiUrl}/api/v2/settings/whatsapp-instances/${encodeURIComponent(instanceName)}`,
      auth(token)
    );
  },

  setPurposes: async (
    token: string,
    instanceName: string,
    clientId: string,
    purposes: WhatsAppInstancePurpose[]
  ): Promise<WhatsAppInstance> => {
    const res = await axios.put(
      `${apiUrl}/api/v2/settings/whatsapp-instances/${encodeURIComponent(instanceName)}/purposes`,
      { client_id: clientId, purposes },
      auth(token)
    );
    return res.data;
  },

  listWebhookCredentials: async (token: string): Promise<WebhookCredential[]> => {
    const res = await axios.get(
      `${apiUrl}/api/v2/settings/whatsapp-webhook/credentials`,
      auth(token)
    );
    return res.data?.credentials || [];
  },

  createWebhookCredential: async (
    token: string,
    payload: { instance_name: string; app_secret: string }
  ): Promise<WebhookCredentialCreated> => {
    const res = await axios.post(
      `${apiUrl}/api/v2/settings/whatsapp-webhook/credentials`,
      payload,
      auth(token)
    );
    return res.data;
  },

  deleteWebhookCredential: async (token: string, instanceName: string): Promise<void> => {
    await axios.delete(
      `${apiUrl}/api/v2/settings/whatsapp-webhook/credentials/${encodeURIComponent(instanceName)}`,
      auth(token)
    );
  },
};

/** How long the pairing dialog keeps asking for a fresh QR before giving up. */
export const QR_POLL_TIMEOUT_MS = 3 * 60 * 1000;
/** Evolution's QR turns over faster than this, so a slower poll shows dead codes. */
export const QR_POLL_INTERVAL_MS = 15 * 1000;
